import { useApolloClient, gql, TypedDocumentNode } from '@apollo/client'
import { cartDetailsFragment, cartFragment } from '@emico-hooks/cart-fragment'
import { useAuthorizationContext } from '@emico-hooks/login-token'
import { useCallback } from 'react'

import {
  SetBillingAddressOnCartMutation,
  SetBillingAddressOnCartMutationVariables,
} from './useSetBillingAddressOnCart.generated'

interface Address {
  firstname: string
  lastname: string
  street: Array<string | null>
  city: string
  postcode?: string | null
  region?: string | null
  region_id?: number | null
  country_code: string
  telephone?: string
  save_in_address_book?: boolean
}

interface BillingAddressInput {
  address?: Address
  customer_address_id?: number
  same_as_shipping?: boolean
  use_for_shipping?: boolean
}

const setBillingAddressOnCartMutation = gql`
  mutation setBillingAddressOnCart(
    $cartId: String!
    $billingAddress: BillingAddressInput!
  ) {
    setBillingAddressOnCart(
      input: { cart_id: $cartId, billing_address: $billingAddress }
    ) {
      cart {
        ...CartFragment
        ...CartDetailsFragment
      }
    }
  }

  ${cartFragment}
  ${cartDetailsFragment}
` as TypedDocumentNode<
  SetBillingAddressOnCartMutation,
  SetBillingAddressOnCartMutationVariables
>

/**
 * Mutation to set a billing address on the cart
 *
 * Usage example:
 *
 * ```ts
 * export function exampleFunction() {
 *  const cartId = useCartId()
 *  const setBillingAddressOnCart = useSetBillingAddressOnCart()
 *
 *  return useCallback(
 *    async () => {
 *      try {
 *        await setBillingAddressOnCart(cartId, { address: billingAddress })
 *      } catch(error) {
 *        // error
 *      }
 *    },
 *    [cartId, setBillingAddressOnCart],
 *  )
 * }
 * ```
 */

export function useSetBillingAddressOnCart() {
  const client = useApolloClient()
  const authorizationContext = useAuthorizationContext()

  return useCallback(
    /**
     *
     * @param cartId Cart id where the address should be added to
     * @param billingAddress  Billing address that should be added to the cart
     */
    async (cartId: string, billingAddress: BillingAddressInput) => {
      const { data } = await client.mutate({
        mutation: setBillingAddressOnCartMutation,
        variables: {
          cartId,
          billingAddress,
        },
        context: authorizationContext,
      })

      return data
    },
    [authorizationContext, client],
  )
}
